// src/App.js for CUSTOMER VIEW (sipnscore-customer)
import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// Screens
import DisclaimerScreen from "./screens/DisclaimerScreen";
import PersonalInfoScreen from "./screens/PersonalInfoScreen";
import PromotionScreen from "./screens/PromotionScreen";
import QuestionnaireScreen from "./screens/QuestionnaireScreen";
import ThankYouScreen from "./screens/ThankYouScreen";
import ScanQRScreen from "./screens/ScanQRScreen";
import VoucherScreen from "./screens/VoucherScreen";
import ClaimScreen from "./screens/ClaimScreen";
import AccessDeniedScreen from "./screens/AccessDeniedScreen";

// Services
import { db } from "./firebase";
import { resolveTeamMember } from "./services/scanPointService";
import { primeQuestions } from "./services/questionsCache";
import {
  getAcceptedDisclaimer,
  setAcceptedDisclaimer,
  getQRData,
  saveQRData,
  getSessionToken,
  saveSessionToken,
  getUserData,
} from "./services/localStorageService";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { useActivationConfig } from "./hooks/useBrand";
import { brandVars, brandFrame, assetStyle } from "./config/branding";
import { useColorScheme } from "./hooks/useColorScheme";

function App() {
  const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [qrData, setQrData] = useState(null);
  // Live, so a colour or logo changed in the portal reaches a phone that is
  // already open on the promotion screen. Falls back to SipNScore before a code
  // has been resolved, and on any error.
  const { brand, guestFields } = useActivationConfig(qrData?.eventId || null);

  // Fetch the questions now, while the guest is still on the disclaimer.
  //
  // They are three taps away from needing them and the id is already known, so
  // there is no reason for the questionnaire to be the screen that waits. This
  // is a no-op if it is already listening for this activation.
  useEffect(() => {
    if (qrData?.eventId) primeQuestions(qrData.eventId);
  }, [qrData?.eventId]);
  // CSS cannot ask "is this artwork dark", so the scheme comes back here and
  // the class is set from it. Live, so a phone switching to dark on a schedule
  // at 18:00 does not leave a guest with the wrong plate.
  const scheme = useColorScheme();
  const logoFrame = brandFrame({
    tone: brand.logoTone,
    transparency: brand.logoTransparency,
    scheme,
    mode: brand.logoPlateMode,
  });
  const [sessionValid, setSessionValid] = useState(true);

  useEffect(() => {
    // Check if disclaimer was already accepted
    const hasAcceptedDisclaimer = getAcceptedDisclaimer();
    setIsDisclaimerAccepted(hasAcceptedDisclaimer);

    // Restore the guest from this device.
    //
    // This used to read Firebase Auth, back when the app created an account per
    // guest with a password derived from their email address. Identity now comes
    // from the registerGuest Cloud Function and is held in local storage for the
    // length of the visit, so there is no guest credential to steal.
    setUserData(getUserData());
    setLoading(false);

    // Parse QR data from URL query params and handle session
    const initSession = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const qrDataParam = queryParams.get("data");
      const idParam = queryParams.get("id");

      // Handle full data format
      if (qrDataParam) {
        try {
          const decodedData = JSON.parse(decodeURIComponent(qrDataParam));
          setQrData(decodedData);
          saveQRData(decodedData);
          if (decodedData.eventId) primeQuestions(decodedData.eventId);
          // Same reasoning as the id branch below: not awaited, because no
          // screen in this load waits on it. Kept in step with that one on
          // purpose, so the two entry points cannot drift.
          createScanSession(decodedData.eventId, decodedData.bartenderId);
          stripUrlParams();
        } catch (error) {
          console.error("Error parsing QR data:", error);
        }
      }
      // Handle simplified ID format (eventId-bartenderId)
      else if (idParam) {
        try {
          const [eventId, bartenderId] = idParam.split("-");

          if (eventId && bartenderId) {
            // The activation id is in the URL, so the questions listener can
            // open now rather than waiting for the two reads below to finish.
            // The effect further up also primes on qrData, which is a no-op
            // once this one is listening; the point is that it used to be the
            // only trigger, and it could not fire until this whole block had
            // resolved. Three screens of head start, for free.
            primeQuestions(eventId);

            // In parallel. These two do not depend on each other, and they were
            // awaited one after the other: on Fast 3G that is an extra round
            // trip of roughly 170ms spent for no reason.
            const [eventDoc, teamMember] = await Promise.all([
              getDoc(doc(db, "events", eventId)),
              resolveTeamMember(eventId, bartenderId),
            ]);

            if (eventDoc.exists()) {
              const eventData = eventDoc.data();

              const fullData = {
                eventId,
                eventName: eventData.name,
                eventBannerUrl: eventData.eventBannerUrl || "",
                bartenderId,
                bartenderName: teamMember.name,
                teamRoleLabel: teamMember.role,
                teamIsPerson: teamMember.person,
                holderKind: teamMember.holderKind,
                holderName: teamMember.holderName,
                promotion: eventData.promotion || "",
                venue: eventData.venue || "",
                featuredBrands: eventData.featuredBrands || [],
                questionsEnabled: eventData.questionsEnabled !== false,
              };

              setQrData(fullData);
              saveQRData(fullData);
              // Not awaited. Nothing in this page load reads what it produces:
              // the only reader of the session token is the returning-visit
              // branch further down, which by definition runs on a later load.
              // Awaiting it put a third round trip in front of the first
              // screen to no purpose.
              //
              // The token is still saved inside only after the write succeeds,
              // deliberately. Saving it first would be marginally faster and
              // would mean a failed write left a token pointing at no document,
              // which the next visit would look up, fail to find, and treat as
              // an invalid session. A guest turned away because our write timed
              // out is a worse trade than 170ms.
              createScanSession(eventId, bartenderId);
              stripUrlParams();
            }
          }
        } catch (error) {
          console.error("Error fetching QR data from IDs:", error);
        }
      }
      // No URL params - check for existing session
      else {
        const existingToken = getSessionToken();
        if (existingToken) {
          const valid = await validateSession(existingToken);
          setSessionValid(valid);
        }
        // Try loading QR data from storage
        const storedQR = getQRData();
        if (storedQR) {
          setQrData(storedQR);
        }
      }
    };

    initSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a scan session in Firestore and save token to sessionStorage
  const createScanSession = async (eventId, bartenderId) => {
    try {
      const token = generateToken();
      const expiresAt = new Date(Date.now() + 30 * 60 * 1000); // 30 minutes

      await addDoc(collection(db, "scanSessions"), {
        eventId,
        bartenderId,
        token,
        createdAt: serverTimestamp(),
        expiresAt,
        used: false,
      });

      saveSessionToken(token);
      setSessionValid(true);
    } catch (error) {
      console.error("Error creating scan session:", error);
    }
  };

  // Validate an existing session token
  const validateSession = async (token) => {
    try {
      const q = query(
        collection(db, "scanSessions"),
        where("token", "==", token)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) return false;

      const session = snapshot.docs[0].data();
      const now = new Date();
      const expiresAt = session.expiresAt?.toDate
        ? session.expiresAt.toDate()
        : new Date(session.expiresAt);

      return now < expiresAt;
    } catch (error) {
      console.error("Error validating session:", error);
      return true; // Fail open to not block users
    }
  };

  // Strip URL parameters for URL masking
  const stripUrlParams = () => {
    const url = new URL(window.location);
    url.search = "";
    window.history.replaceState({}, document.title, url.pathname);
  };

  // Generate a random session token
  const generateToken = () => {
    return (
      Math.random().toString(36).substring(2) +
      Date.now().toString(36)
    );
  };

  const handleDisclaimerAccept = () => {
    setAcceptedDisclaimer(true);
    setIsDisclaimerAccepted(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
      {/* The brand paints the whole flow, not just the promotion screen. A
          guest who sees a client's colours on the promotion and SipNScore's on
          the questions has been shown two different things by one QR code. The
          variables are set here, once, and every screen below reads them. */}
      <div className="app-container" style={brandVars(brand)}>
        <div
          className="app-logo"
          style={logoFrame.plated ? { background: logoFrame.background } : undefined}
        >
          {brand.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt={brand.name}
              className="app-logo__img"
              style={assetStyle(logoFrame.fit || brand.logoFit, brand.logoScale)}
            />
          ) : (
            <h1>SipNScore</h1>
          )}
        </div>
        <div className="app-content">
          <Routes>
            {/* Main route - handles flow control */}
            <Route
              path="/"
              element={
                !sessionValid ? (
                  <AccessDeniedScreen />
                ) : isDisclaimerAccepted ? (
                  userData ? (
                    qrData ? (
                      <PromotionScreen qrData={qrData} userData={userData} />
                    ) : (
                      <ScanQRScreen setQrData={setQrData} />
                    )
                  ) : (
                    <PersonalInfoScreen
                      setUserData={setUserData}
                      guestFields={guestFields}
                      eventId={qrData?.eventId || null}
                    />
                  )
                ) : (
                  <DisclaimerScreen onAccept={handleDisclaimerAccept} />
                )
              }
            />

            {/* Scan route - handles direct QR code scans */}
            <Route
              path="/scan"
              element={
                isDisclaimerAccepted ? (
                  userData ? (
                    <PromotionScreen qrData={qrData} userData={userData} />
                  ) : (
                    <PersonalInfoScreen
                      setUserData={setUserData}
                      guestFields={guestFields}
                      eventId={qrData?.eventId || null}
                    />
                  )
                ) : (
                  <DisclaimerScreen onAccept={handleDisclaimerAccept} />
                )
              }
            />

            {/* Other specific routes */}
            <Route
              path="/disclaimer"
              element={<DisclaimerScreen onAccept={handleDisclaimerAccept} />}
            />
            <Route
              path="/personal-info"
              element={<PersonalInfoScreen
                      setUserData={setUserData}
                      guestFields={guestFields}
                      eventId={qrData?.eventId || null}
                    />}
            />
            <Route
              path="/promotion"
              element={<PromotionScreen qrData={qrData} userData={userData} />}
            />
            <Route
              path="/questionnaire"
              element={
                <QuestionnaireScreen qrData={qrData} userData={userData} />
              }
            />
            <Route path="/thank-you" element={<ThankYouScreen onDone={() => setQrData(null)} />} />
            <Route path="/voucher" element={<VoucherScreen />} />
            <Route path="/claim" element={<ClaimScreen />} />
            <Route path="/access-denied" element={<AccessDeniedScreen />} />
          </Routes>
        </div>
        <footer className="app-footer">
          {/* Only when the activation wears somebody else's brand. On our own
              brand this would say SipNScore twice in three lines. */}
          {brand.poweredBy && brand.logoUrl && (
            <div className="powered-by">
              <span>powered by</span>
              {/* The mark, not the word. Two files rather than a CSS filter:
                  the wordmark is black script with a red N, and inverting it
                  turns the red cyan. The dark variant lifts only the script. */}
              <img
                src={scheme === "dark" ? "/logo-mark-dark.png" : "/logo-mark.png"}
                alt="SipNScore"
                className="powered-by__mark"
              />
            </div>
          )}
          <p>
            &copy; {new Date().getFullYear()} SipNScore. All rights reserved.
          </p>
        </footer>
      </div>
    </Router>
  );
}

export default App;
